<template>
  <b-card
    v-if="data"
    no-body
    class="citywise-delivery-card"
  >
    <b-card-header class="citywise-delivery-header">
      <h4 class="chart-title mb-0">
        Citywise Delivery Statistics
      </h4>
      <div class="header-actions">
        <small
          v-if="lastUpdatedAt"
          class="timestamp-badge"
          :class="{ 'clickable': onRefresh }"
          @click="handleRefresh"
        >
          <feather-icon
            icon="RefreshCwIcon"
            size="12"
            class="mr-1"
            :class="{ 'spin': isRefreshing }"
          />
          <span v-if="lastRefreshTimeAgo">
            {{ lastRefreshTimeAgo }}
          </span>
          <span v-else>
            {{ __dateTimeFormatterPassedTime(lastUpdatedAt) }}
          </span>
        </small>
      </div>
    </b-card-header>
    <b-card-body class="citywise-delivery-body">
      <div class="mo-chart-scroll">
        <div class="mo-chart-wrapper mo-chart-min-width">
          <vue-apex-charts
            id="citywise-delivery-chart"
            type="bar"
            height="450"
            :options="chartOptions"
            :series="data.series"
          />
        </div>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard, BCardHeader, BCardBody,
} from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'

export default {
  components: {
    VueApexCharts,
    BCard,
    BCardHeader,
    BCardBody,
  },
  props: {
    data: {
      type: Object,
      default: () => {},
    },
    lastUpdatedAt: {
      type: String,
      default: null,
    },
    onRefresh: {
      type: Function,
      default: null,
    },
    isRefreshing: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentTime: Date.now(),
      updateInterval: null,
    }
  },
  computed: {
    lastRefreshTimeAgo() {
      try {
        const refreshTimes = JSON.parse(localStorage.getItem('dashboardwidgetsrefreshtimes') || '{}')
        const lastRefreshTime = refreshTimes.citywisedeliverystatistics
        if (!lastRefreshTime) return null

        const lastRefresh = new Date(lastRefreshTime).getTime()
        const now = this.currentTime
        const diff = now - lastRefresh

        if (diff < 0) return null

        const seconds = Math.floor(diff / 1000)
        const minutes = Math.floor(seconds / 60)
        const hours = Math.floor(minutes / 60)
        const days = Math.floor(hours / 24)
        const months = Math.floor(days / 30)
        const years = Math.floor(months / 12)

        if (minutes < 1) return 'just now'
        if (minutes < 60) return `${minutes} minutes ago`
        if (hours < 24) return `${hours} hours ago`
        if (days < 30) return `${days} days ago`
        if (months < 12) return `${months} months ago`
        return `${years} years ago`
      } catch (e) {
        return null
      }
    },
    chartOptions() {
      // Capture warehouse names for use in tooltip
      const warehouseNames = this.data?.warehouseNames || []

      return {
        chart: {
          type: 'bar',
          toolbar: { show: false },
          zoom: { enabled: false },
        },
        plotOptions: {
          bar: {
            columnWidth: '18px',
            borderRadius: 6,
            borderRadiusApplication: 'end',
            dataLabels: {
              position: 'top',
            },
          },
        },
        dataLabels: {
          enabled: false,
        },
        colors: ['#00A300'],
        fill: {
          type: 'gradient',
          gradient: {
            shade: 'light',
            type: 'vertical',
            shadeIntensity: 0.5,
            gradientToColors: ['#4CFF00'],
            inverseColors: false,
            opacityFrom: 1,
            opacityTo: 1,
            stops: [0, 100],
          },
        },
        grid: {
          borderColor: '#f1f1f1',
          strokeDashArray: 4,
          padding: {
            bottom: 20,
          },
          yaxis: {
            lines: {
              show: true,
            },
          },
          xaxis: {
            lines: {
              show: false,
            },
          },
        },
        xaxis: {
          categories: this.data?.categories?.lables || [],
          labels: {
            show: true,
            rotate: 0,
            rotateAlways: false,
            hideOverlappingLabels: false,
            style: {
              colors: '#6b7280',
              fontSize: '11px',
              fontFamily: 'Roboto, sans-serif',
            },
            offsetY: 0,
          },
          axisTicks: {
            show: false,
          },
          axisBorder: {
            show: false,
          },
        },
        yaxis: {
          labels: {
            formatter(value) {
              return Math.round(value).toLocaleString()
            },
            style: {
              colors: '#9ca3af',
              fontSize: '10px',
              fontFamily: 'Roboto, sans-serif',
            },
            align: 'right',
            offsetX: -5,
          },
        },
        tooltip: {
          shared: false,
          intersect: true,
          enabled: true,
          style: {
            fontSize: '11px',
            fontFamily: 'Roboto, sans-serif',
          },
          custom({ seriesIndex, dataPointIndex, w }) {
            const warehouseName = warehouseNames[dataPointIndex] || ''
            const value = w.globals.series[seriesIndex][dataPointIndex] || 0

            const tooltipContent = `
              <div style="background-color: #00C800; border: 1px solid #00A300; color: #ffffff; padding: 10px 14px; border-radius: 6px; font-family: 'Roboto', sans-serif; font-size: 11px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15); min-width: 180px;">
                <div style="font-weight: 600; margin-bottom: 8px; line-height: 1.4; font-size: 12px; border-bottom: 1px solid rgba(255, 255, 255, 0.2); padding-bottom: 6px;">Delivery Details</div>
                <div style="margin-bottom: 6px; line-height: 1.5;">
                  <span style="opacity: 0.9; font-weight: 500;">Warehouse:</span>
                  <span style="font-weight: 600; margin-left: 6px;">${warehouseName}</span>
                </div>
                <div style="margin-top: 8px; padding-top: 6px; border-top: 1px solid rgba(255, 255, 255, 0.2); line-height: 1.5;">
                  <span style="opacity: 0.9; font-weight: 500;">Count:</span>
                  <span style="font-weight: 600; margin-left: 6px; font-size: 13px;">${value.toLocaleString()}</span>
                </div>
              </div>
            `

            return tooltipContent
          },
        },
      }
    },
  },
  mounted() {
    // Update countdown every second
    this.updateInterval = setInterval(() => {
      this.currentTime = Date.now()
    }, 1000)
  },
  beforeDestroy() {
    if (this.updateInterval) {
      clearInterval(this.updateInterval)
    }
  },
  methods: {
    handleRefresh() {
      if (this.onRefresh && !this.isRefreshing) {
        this.onRefresh()
      }
    },
  },
}
</script>

<style scoped>
.citywise-delivery-card {
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
  border-radius: 0.857rem;
  overflow: hidden;
  height: 100%;
  transition: all 0.3s ease;
}

.citywise-delivery-card:hover {
  box-shadow: 0 8px 32px 0 rgba(34, 41, 47, 0.15);
  transform: translateY(-2px);
}

.mo-chart-scroll {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
}

.mo-chart-min-width {
  min-width: 800px;
}

.citywise-delivery-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  background: #fff;
  border-bottom: none;
}

.chart-title {
  font-size: 1.285rem;
  font-weight: 600;
  color: #5e5873;
  margin: 0;
  font-family: 'Roboto', sans-serif;
}

.header-actions {
  display: flex;
  align-items: center;
}

.timestamp-badge {
  display: flex;
  align-items: center;
  background-color: #f5f5f5;
  color: #6e6b7b;
  padding: 0.375rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  transition: all 0.2s ease;
  font-family: 'Roboto', sans-serif;
}

.timestamp-badge.clickable {
  cursor: pointer;
  user-select: none;
}

.timestamp-badge.clickable:hover {
  background-color: #e8e8e8;
  transform: scale(1.05);
}

.timestamp-badge.clickable:active {
  transform: scale(0.98);
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.citywise-delivery-body {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  flex: 1;
}

@media (max-width: 768px) {
  .citywise-delivery-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
}
</style>
